import React, { useState, useRef, useEffect } from 'react';
import Icon from '../common/Icon';
import { foldersAPI } from '../../services/api';
import './FolderSidebar.css';

const DEFAULT_COLORS = ['#00BCBC', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6', '#14B8A6'];

const FolderSidebar = ({ folders, selectedFolderId, onSelectFolder, onFoldersChange }) => {
    const [isCreating, setIsCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [selectedColor, setSelectedColor] = useState(DEFAULT_COLORS[0]);
    const [editingFolder, setEditingFolder] = useState(null);
    const [editName, setEditName] = useState('');
    const [hoveredFolder, setHoveredFolder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const newInputRef = useRef(null);
    const editInputRef = useRef(null);

    const filteredFolders = folders.filter(f =>
        f.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (isCreating && newInputRef.current) {
            newInputRef.current.focus();
        }
    }, [isCreating]);

    useEffect(() => {
        if (editingFolder && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingFolder]);

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;

        setLoading(true);
        try {
            await foldersAPI.create({ name, color: selectedColor });
            await onFoldersChange();
            setIsCreating(false);
            setNewFolderName('');
            setSelectedColor(DEFAULT_COLORS[0]);
        } catch (error) {
            console.error('Erreur création dossier:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKeyDown = (e) => {
        if (e.key === 'Enter') handleCreateFolder();
        if (e.key === 'Escape') {
            setIsCreating(false);
            setNewFolderName('');
        }
    };

    const handleStartEdit = (folder) => {
        setEditingFolder(folder.folderId);
        setEditName(folder.name);
    };

    const handleSaveEdit = async (folderId) => {
        const name = editName.trim();
        if (!name) {
            setEditingFolder(null);
            return;
        }

        const folder = folders.find(f => f.folderId === folderId);
        setLoading(true);
        try {
            await foldersAPI.update(folderId, { name, color: folder?.color });
            await onFoldersChange();
        } catch (error) {
            console.error('Erreur mise à jour dossier:', error);
        } finally {
            setLoading(false);
            setEditingFolder(null);
        }
    };

    const handleEditKeyDown = (e, folderId) => {
        if (e.key === 'Enter') handleSaveEdit(folderId);
        if (e.key === 'Escape') setEditingFolder(null);
    };

    const handleDeleteFolder = async (e, folderId) => {
        e.stopPropagation();
        if (!window.confirm('Supprimer ce dossier ? Les études seront conservées mais non-assignées.')) return;

        setLoading(true);
        try {
            await foldersAPI.delete(folderId);
            if (selectedFolderId === folderId) {
                onSelectFolder(null);
            }
            await onFoldersChange();
        } catch (error) {
            console.error('Erreur suppression dossier:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
    <>
        <aside className="folder-sidebar">
            <div className="folder-sidebar-header">
                <span className="folder-sidebar-title">Dossiers</span>
                <button
                    className="folder-add-btn"
                    onClick={() => setIsCreating(true)}
                    title="Nouveau dossier"
                    disabled={loading}
                >
                    <Icon name="Plus" size={16} />
                </button>
            </div>

            {folders.length > 0 && (
                <div className="folder-search-wrapper">
                    <Icon name="Search" size={14} color="var(--color-gray-400)" />
                    <input
                        className="folder-search-input"
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="folder-search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="X" size={12} />
                        </button>
                    )}
                </div>
            )}

            <nav className="folder-nav">
                <div className="folder-nav-static">
                    <button
                        className={`folder-nav-item ${selectedFolderId === null ? 'active' : ''}`}
                        onClick={() => onSelectFolder(null)}
                    >
                        <Icon name="LayoutGrid" size={16} />
                        <span>Tous les projets</span>
                    </button>

                    <button
                        className={`folder-nav-item ${selectedFolderId === 'unassigned' ? 'active' : ''}`}
                        onClick={() => onSelectFolder('unassigned')}
                    >
                        <Icon name="Inbox" size={16} />
                        <span>Sans dossier</span>
                    </button>

                    {folders.length > 0 && <div className="folder-separator" />}
                </div>

                <div className="folder-nav-scrollable">
                {filteredFolders.map((folder) => (
                    <div
                        key={folder.folderId}
                        className={`folder-nav-item-wrapper ${selectedFolderId === folder.folderId ? 'active' : ''}`}
                        onMouseEnter={() => setHoveredFolder(folder.folderId)}
                        onMouseLeave={() => setHoveredFolder(null)}
                    >
                        {editingFolder === folder.folderId ? (
                            <div className="folder-edit-row">
                                <span
                                    className="folder-color-dot"
                                    style={{ backgroundColor: folder.color || 'var(--color-primary)' }}
                                />
                                <input
                                    ref={editInputRef}
                                    className="folder-edit-input"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => handleEditKeyDown(e, folder.folderId)}
                                    onBlur={() => handleSaveEdit(folder.folderId)}
                                />
                            </div>
                        ) : (
                            <button
                                className={`folder-nav-item ${selectedFolderId === folder.folderId ? 'active' : ''}`}
                                onClick={() => onSelectFolder(folder.folderId)}
                            >
                                <span
                                    className="folder-color-dot"
                                    style={{ backgroundColor: folder.color || 'var(--color-primary)' }}
                                />
                                <span className="folder-name">{folder.name}</span>
                                <span className="folder-count">{folder.studyCount}</span>
                            </button>
                        )}

                        {hoveredFolder === folder.folderId && editingFolder !== folder.folderId && (
                            <div className="folder-actions">
                                <button
                                    className="folder-action-btn"
                                    onClick={(e) => { e.stopPropagation(); handleStartEdit(folder); }}
                                    title="Renommer"
                                >
                                    <Icon name="Pencil" size={13} />
                                </button>
                                <button
                                    className="folder-action-btn danger"
                                    onClick={(e) => handleDeleteFolder(e, folder.folderId)}
                                    title="Supprimer"
                                >
                                    <Icon name="Trash2" size={13} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                {searchTerm && filteredFolders.length === 0 && (
                    <p className="folder-no-results">Aucun dossier trouvé</p>
                )}
                </div>
            </nav>
        </aside>

        {isCreating && (
            <div className="folder-modal-overlay" onClick={() => { setIsCreating(false); setNewFolderName(''); setSelectedColor(DEFAULT_COLORS[0]); }}>
                <div className="folder-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="folder-modal-header">
                        <h3 className="folder-modal-title">Nouveau dossier</h3>
                        <button
                            className="folder-modal-close"
                            onClick={() => { setIsCreating(false); setNewFolderName(''); setSelectedColor(DEFAULT_COLORS[0]); }}
                        >
                            <Icon name="X" size={18} />
                        </button>
                    </div>

                    <div className="folder-modal-body">
                        <label className="folder-modal-label">Nom du dossier</label>
                        <input
                            ref={newInputRef}
                            className="folder-modal-input"
                            placeholder="Ex: Clients e-commerce"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={handleCreateKeyDown}
                        />

                        <label className="folder-modal-label">Couleur</label>
                        <div className="folder-modal-colors">
                            {DEFAULT_COLORS.map(color => (
                                <button
                                    key={color}
                                    className={`folder-modal-color-btn ${selectedColor === color ? 'selected' : ''}`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => setSelectedColor(color)}
                                />
                            ))}
                        </div>

                        {newFolderName.trim() && (
                            <div className="folder-modal-preview">
                                <span className="folder-color-dot" style={{ backgroundColor: selectedColor }} />
                                <span>{newFolderName.trim()}</span>
                            </div>
                        )}
                    </div>

                    <div className="folder-modal-footer">
                        <button
                            className="folder-modal-btn cancel"
                            onClick={() => { setIsCreating(false); setNewFolderName(''); setSelectedColor(DEFAULT_COLORS[0]); }}
                        >
                            Annuler
                        </button>
                        <button
                            className="folder-modal-btn confirm"
                            onClick={handleCreateFolder}
                            disabled={!newFolderName.trim() || loading}
                        >
                            {loading ? 'Création...' : 'Créer le dossier'}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};

export default FolderSidebar;
